<script setup lang="ts">
import {ProductSummary} from "@/product/interfaces/ProductSummary";
import {TopSale} from "@/core/interfaces/sales/Sale";
import {Checkbox} from "@/core/components/ui/checkbox";
import dateHelper from "../../../../core/helpers/dateHelper";
import {Check} from '@lucide/vue';
import TopSaleTimeline from "@/drug-interaction/components/drug-selection/raw/TopSaleTimeline.vue";
import dayjs from "dayjs";
import {computed} from "vue";
import {Dialog, DialogContent, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import SecureImage from "@/core/components/secure-image/SecureImage.vue";
import SecurePrescriptionPages from "@/core/components/secure-image/SecurePrescriptionPages.vue";
import {Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious} from "@/core/components/ui/carousel";

const props = defineProps<{
    product: ProductSummary,
    history?: TopSale[];
    selected?: boolean;
}>();

const emit = defineEmits<{
    (e: 'select', product: ProductSummary): void;
}>();

const from = dayjs().subtract(1, 'year').startOf('month').toISOString();
const to = dayjs().endOf('month').toISOString();

const hasPrescriptions = computed(() => {
    return props.history && props.history.some((sale) => sale.prescription);
});

const prescriptions = computed(() => {
    if (!props.history) {
        return [];
    }

    return props.history?.filter((sale) => sale.prescription)
        .map(sale => sale.prescription)
        .filter(prescription => !!prescription) ?? [];
});
</script>

<template>
  <div class="flex gap-8">
    <div class="rounded-md border-[1px] border-black flex-1 shadow-md overflow-hidden self-start bg-white">
      <div class="flex p-3 gap-3">
        <Checkbox
          :id="product?.id.toString()"
          class="h-6 w-6 mt-1"
          :model-value="selected"
          @update:model-value="emit('select', product)"
        >
          <Check class="w-6 h-6" />
        </Checkbox>
        <div class="flex-1">
          <label class="font-medium text-lg" :for="product?.id.toString()">
            {{ product?.name ?? 'Produit inconnu' }}
          </label>
          <p>
            {{ product.cip }}
          </p>
        </div>
        <div v-if="history?.length" class="text-end">
          <p>Dernière délivrance</p>
          <p class="font-bold text-primary">
            {{ dateHelper.shortFormat(history[0].date) }}
          </p>
        </div>
      </div>
      <div v-if="history?.length" class="px-3 py-2 flex flex-col lg:flex-row items-center">
        <p><span class="font-bold text-lg">{{ history.length }}</span> {{ $filters.pluralize('Délivrance', history.length) }} en 1 an</p>
        <div class="max-w-3xl mx-auto px-8 w-full">
          <TopSaleTimeline
            :sales="history"
            :from="from"
            :to="to"
          />
        </div>
      </div>
    </div>
    <div v-if="hasPrescriptions && !!prescriptions[0]" class="max-h-full">
      <Dialog>
        <DialogTrigger>
          <SecureImage :urls="prescriptions[0].files" :size="32" :ratio="0.7" prevent-click />
        </DialogTrigger>
        <DialogContent class="min-w-xl max-w-2xl">
          <DialogTitle class="text-lg font-semibold mb-4">
            Ordonnance pour {{ product?.name }}
          </DialogTitle>
          <div class="flex flex-col items-center">
            <Carousel>
              <CarouselContent>
                <CarouselItem v-for="prescription in prescriptions" :key="prescription.key">
                  <SecurePrescriptionPages :urls="prescription.files" :size="82" />
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious v-if="prescriptions.length > 1" />
              <CarouselNext v-if="prescriptions.length > 1" />
            </Carousel>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  </div>
</template>

<style scoped>

</style>
